import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { useDataTableState } from "@trillion/ui";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin-api";
import { getIntentColumns } from "@/components/intents/intent-columns";
import type { TrafficIntent } from "@/types";

const PAGE_SIZE = 20;

export function IntentsPage() {
  const navigate = useNavigate();
  const [sourceTypeFilter, setSourceTypeFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");

  const t = useDataTableState<TrafficIntent>({
    fetcher: async (p) => {
      const data = await adminApi.intents.list({
        page: p.page,
        pageSize: p.pageSize,
        sourceType: (p.filters.sourceType as string | undefined) || undefined,
        topic: (p.filters.topic as string | undefined) || undefined,
      });
      return { items: data.items, totalCount: data.total_count };
    },
    onDelete: async (intent) => {
      await adminApi.intents.delete(intent.traffic_intent_id);
      toast.success("Intent deleted successfully");
    },
    filters: { sourceType: sourceTypeFilter || undefined, topic: topicFilter || undefined },
    pageSize: PAGE_SIZE,
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to load intents"),
  });

  const { setDeleteTarget } = t;
  const columns = useMemo(
    () =>
      getIntentColumns({
        onEdit: (intent) => navigate(`/intents/${intent.traffic_intent_id}`),
        onDelete: (intent) => setDeleteTarget(intent),
      }),
    [navigate, setDeleteTarget]
  );

  const table = useReactTable({
    data: t.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col">
      <AdminHeader
        title="Intents"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Intents" }]}
      />
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="Traffic Intents"
          description={`${t.totalCount} total intents`}
          actions={
            <Button onClick={() => navigate("/intents/new")}>
              <Plus className="size-4" />
              New Intent
            </Button>
          }
        />

        <DataTableWrapper
          title="Intents"
          description="Manage classified traffic intents"
          isLoading={t.isLoading}
          toolbar={
            <div className="flex items-center gap-3">
              <Input
                placeholder="Filter by topic..."
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                className="max-w-xs"
              />
              <Select
                value={sourceTypeFilter || "_all"}
                onValueChange={(val) => setSourceTypeFilter(val === "_all" ? "" : val)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Source type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All types</SelectItem>
                  <SelectItem value="domain">Domain</SelectItem>
                  <SelectItem value="referrer">Referrer</SelectItem>
                  <SelectItem value="utm">UTM</SelectItem>
                  <SelectItem value="direct">Direct</SelectItem>
                  <SelectItem value="social">Social</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
          pagination={
            t.totalPages > 1 ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {t.page} of {t.totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => t.setPage(Math.max(1, t.page - 1))}
                    disabled={t.page <= 1}
                  >
                    <ChevronLeft className="size-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => t.setPage(Math.min(t.totalPages, t.page + 1))}
                    disabled={t.page >= t.totalPages}
                  >
                    Next
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
            ) : undefined
          }
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No intents found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DataTableWrapper>
      </div>

      <ConfirmDialog
        open={t.deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) t.setDeleteTarget(null);
        }}
        title="Delete Intent"
        description={`Are you sure you want to delete the intent for "${t.deleteTarget?.source_key ?? ""}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={t.confirmDelete}
        variant="destructive"
        isLoading={t.isDeleting}
      />
    </div>
  );
}
